import React, { useState } from 'react'
import { Link, useMatchRoute } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Globe,
  ShieldCheck,
  KeyRound,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { useMe } from '@/lib/use-me'
import { ThemeSwitcher } from '@/components/ui/theme-switcher'

interface NavItem {
  label: string
  to: string
  icon: React.ComponentType<{ className?: string }>
  // When true the item is only shown to admins (e.g. Certificates — customers
  // have no certificate access at all).
  adminOnly?: boolean
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'DNS Records', to: '/dns', icon: Globe },
  { label: 'Certificates', to: '/certificates', icon: ShieldCheck, adminOnly: true },
  { label: 'Auth & Tokens', to: '/auth', icon: KeyRound },
]

interface AppShellProps {
  children: React.ReactNode
  title: string
}

function NavLink({ item, expanded }: { item: NavItem; expanded: boolean }) {
  const matchRoute = useMatchRoute()
  const isActive = !!matchRoute({ to: item.to, fuzzy: true })
  const Icon = item.icon

  return (
    <Link
      to={item.to}
      title={!expanded ? item.label : undefined}
      className={cn(
        'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 group',
        isActive
          ? 'bg-[var(--primary)]/10 text-[var(--fg)]'
          : 'text-[var(--fg-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg)]',
        !expanded && 'justify-center'
      )}
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-[var(--primary)]" />
      )}
      <Icon
        className={cn(
          'h-[18px] w-[18px] shrink-0 transition-colors duration-150',
          isActive ? 'text-[var(--primary)]' : 'text-[var(--fg-muted)] group-hover:text-[var(--fg)]'
        )}
      />
      {expanded && (
        <>
          <span className={cn('flex-1 truncate', isActive && 'font-semibold')}>{item.label}</span>
          {isActive && (
            <ChevronRight className="h-3.5 w-3.5 text-[var(--primary)] opacity-50" />
          )}
        </>
      )}
    </Link>
  )
}

function TdLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
      <rect width="32" height="32" rx="8" fill="url(#td-logo-grad)" />
      <path d="M8 10h16M16 10v12" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <defs>
        <linearGradient id="td-logo-grad" x1="0" y1="0" x2="32" y2="32">
          <stop stopColor="#2563eb" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  )
}

export function AppShell({ children, title }: AppShellProps) {
  const [expanded, setExpanded] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { logout } = useAuth()
  const { isAdmin, cloudCustomerId } = useMe()

  // Customers never see admin-only surfaces (e.g. Certificates).
  const visibleNavItems = navItems.filter((item) => isAdmin || !item.adminOnly)

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--bg)]">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col border-r border-[var(--border)] bg-[var(--bg)] transition-all duration-300',
          expanded ? 'w-64' : 'w-16'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-[var(--border)]">
          {expanded ? (
            <>
              <div className="flex items-center gap-2">
                <TdLogo />
                <span className="font-semibold text-[var(--fg)]">AIP DNS/Cert</span>
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="p-1 rounded hover:bg-[var(--bg-elevated)]"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setExpanded(true)}
              className="p-1 rounded hover:bg-[var(--bg-elevated)]"
            >
              <Menu className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink key={item.to} item={item} expanded={expanded} />
          ))}
        </nav>

        <div className="border-t border-[var(--border)] p-3 space-y-2">
          <ThemeSwitcher />
          {expanded && (
            <button
              onClick={logout}
              className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-[var(--fg-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg)] transition-colors"
            >
              <LogOut className="h-[18px] w-[18px]" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col border-r border-[var(--border)] bg-[var(--bg)] md:hidden">
            <div className="flex h-16 items-center justify-between px-4 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <TdLogo />
                <span className="font-semibold text-[var(--fg)]">AIP DNS/Cert</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 rounded hover:bg-[var(--bg-elevated)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
              {visibleNavItems.map((item) => (
                <NavLink key={item.to} item={item} expanded={true} />
              ))}
            </nav>

            <div className="border-t border-[var(--border)] p-3 space-y-2">
              <ThemeSwitcher />
              <button
                onClick={logout}
                className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-[var(--fg-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--fg)] transition-colors"
              >
                <LogOut className="h-[18px] w-[18px]" />
                <span>Logout</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b border-[var(--border)] bg-[var(--bg)] flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded hover:bg-[var(--bg-elevated)]"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold text-[var(--fg)]">{title}</h1>
          </div>
          {cloudCustomerId && (
            <span
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1 text-xs font-medium text-[var(--fg-muted)]"
              title="Your cloud_customer_id (owning tenant)"
            >
              <span className="text-[var(--fg-muted)]">Tenant</span>
              <span className="font-semibold text-[var(--fg)] tabular-nums">{cloudCustomerId}</span>
              {isAdmin && (
                <span className="ml-1 rounded bg-[var(--primary)]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[var(--primary)]">
                  admin
                </span>
              )}
            </span>
          )}
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
